import PropTypes from 'prop-types';
import React from 'react';
import padPrice from '../../../utils/padPrice';
import { sendAnalyticsEvent } from '../../../utils/analytics';
import FeaturedInModule from './featured_in_module';
import RosettaFormattedMessage from '../../common/rosetta_formatted_message';
import SocialIcons from '../../common/social_icons';
import ProductLink from '../../common/product_link';
import Link from 'react-router/lib/Link';
import getCapitalizedName from '../../../utils/getCapitalizedName';
import {get, last, isFinite} from 'lodash';
import { smoothScrollTo, getElementById } from '../../../utils/dom';

export default function ProductDetailsInfo(props) {
  const {
    product: { id, brand, name, product_url, images, description, is_sponsored,
      click_tracker, impression_tracker, entries, offer: { currency, price,
        sale_price, retailer }, is_sem3_available
    },
    meta_data: { meta_title, meta_description },
    analytics: { url_full },
    shoppingNavigation: { breadcrumbs }
  } = props;

  const handleImpressionTrackerLoad = () =>
    sendAnalyticsEvent('Shopping', 'Impression', id, { nonInteraction: true });

  const handlePurchaseClick = () => {
    sendAnalyticsEvent('Shopping', `Buy${click_tracker ? ' ClickTracker' : ''}`, id, { nonInteraction: false });
  };

  const handleReviewItButtonClick = () => {
    smoothScrollTo(getElementById('product-detail-comments', document));
  };

  const productFirstCategory = last(breadcrumbs);

  const isAvailable = is_sem3_available;

  let sponsoredLabel;
  if (is_sponsored) {
    sponsoredLabel = <div className="sponsored-label" dangerouslySetInnerHTML={{ __html: 'promoted' }} />;
  }

  let impressionTracker;
  if (impression_tracker) {
    impressionTracker = (
      <img className="product-details-impression-tracker no-content"
        onLoad={handleImpressionTrackerLoad}
        src={impression_tracker}
      />
    );
  }

  let featuredEntries;
  if (entries && entries.length > 0) {
    featuredEntries = <FeaturedInModule entries={entries}/>;
  }

  const shareImage = get(images, '[0].url');
  const socialIconProps = {
    sanitizedTitle: meta_title,
    url_full: url_full,
    enableAdTextForShares: is_sponsored,
    asset_main: { src_full: shareImage },
    meta_description: meta_description,
    meta_title: meta_title
  };

  const productLinkProps = {
    click_tracker,
    product_url,
    delegatedOnClick: handlePurchaseClick
  };

  const retailerName = (
    <RosettaFormattedMessage parentFilename={__filename}
      id="retailer_name"
      description="Message that tells users where to buy the product"
      defaultMessage="At {retailer_name}"
      values={{ retailer_name: get(retailer, 'name') }}
    >
      {(__html) => <span dangerouslySetInnerHTML={{ __html }} />}
    </RosettaFormattedMessage>
  );

  const retailerNameUnavailable = (
    <RosettaFormattedMessage parentFilename={__filename}
      id="product_unavailable_retailer_name"
      description="Message that tells users where this product was available to buy"
      defaultMessage="Was available at {retailer_name}"
      values={{ retailer_name: get(retailer, 'name') }}
    >
      {(__html) => <span dangerouslySetInnerHTML={{ __html }} />}
    </RosettaFormattedMessage>
  );

  const genericSeeMoreMessage = (
    <RosettaFormattedMessage parentFilename={__filename}
      id="product_unavailable_shop_more"
      description="Message that appears on product unavailable button when first category is Shop"
      defaultMessage="Find similar products"
    />
  );

  const categorySpecificSeeMoreMessage = (
    <RosettaFormattedMessage parentFilename={__filename}
      id="product_unavailable_see_more"
      description="Message that appears on product unavailable button, link to first category"
      values={{ first_category: productFirstCategory.name }}
      defaultMessage="See more {first_category}"
    />
  );

  const availableProductInfo = (
    <div className="available-product-info">
      <ProductLink {...productLinkProps}>
        <button className="buy-button">
          <RosettaFormattedMessage parentFilename={__filename}
            id="buy_button"
            description="Message that appears on product detail page buy button"
            defaultMessage="Buy Now"
          />
        </button>
      </ProductLink>
      <button className="review-it-button" onClick={handleReviewItButtonClick}>
        <RosettaFormattedMessage parentFilename={__filename}
          id="review_button"
          description="Message that appears on product detail page review it button"
          defaultMessage="Review It"
        />
      </button>
    </div>
  );

  const unavailableProductInfo = (
    <div className="unavailable-product-info">
      <div className="unavailable-product-msg">
        <RosettaFormattedMessage parentFilename={__filename}
          id="product_unavailable_featured_message"
          description="Message that tells users the product is unavailable"
          defaultMessage="We're sorry, looks like this item is sold out"
        >
          {(__html) => <span dangerouslySetInnerHTML={{ __html }} />}
        </RosettaFormattedMessage>
      </div>
      <Link to={productFirstCategory.path} >
        <button
          className="see-more-button"
        >
          {
            productFirstCategory.name === 'Shop' ? genericSeeMoreMessage : categorySpecificSeeMoreMessage
          }
        </button>
      </Link>
    </div>
  );

  const retailerPath = get(retailer, 'path', null);

  const brandName = (<span dangerouslySetInnerHTML={{ __html: get(brand, 'name') }} />);
  const brandPath = get(brand, 'path', null);

  return (
    <div id="product-details-information" className={isFinite(sale_price) ? 'sale' : ''}>
      {impressionTracker}
      {sponsoredLabel}
      <div className="brand-name">
        {
          brandPath ?
            (
              <Link to={brandPath} target="_blank">
                {brandName}
              </Link>
            )
            : (
              <div>
                {brandName}
              </div>
            )
        }
      </div>
      <h1 className="name" dangerouslySetInnerHTML={{ __html: getCapitalizedName(name) }} />
      <div className="price">
        <span className="retail-price">{`${padPrice(currency, price)}`}</span>
        <span className="sale-price">{`${padPrice(currency, sale_price)}`}</span>
      </div>

      <div className="product-buttons">
        {
          isAvailable ? availableProductInfo : unavailableProductInfo
        }
      </div>

      <div className="retailer-name">
        {
          retailerPath ?
            (
              <Link to={retailerPath} target="_blank">
                {isAvailable ? retailerName : retailerNameUnavailable}
              </Link>
            )
            : (
              <div>
                {isAvailable ? retailerName : retailerNameUnavailable}
              </div>
            )
        }
      </div>
      <div className="description"
        dangerouslySetInnerHTML={{ __html: description }}
      />
      <SocialIcons {...socialIconProps}
        className="product-social"
        icons={['facebook', 'pinterest', 'email']}
      />
      {featuredEntries}
    </div>
  );
}

ProductDetailsInfo.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    brand: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      path: PropTypes.string
    }),
    name: PropTypes.string.isRequired,
    product_url: PropTypes.string.isRequired,
    offer: PropTypes.shape({
      currency: PropTypes.string,
      price: PropTypes.number,
      sale_price: PropTypes.number,
      retailer: PropTypes.shape({
        id: PropTypes.number,
        url: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        path: PropTypes.string
      })
    }),
    images: PropTypes.array,
    description: PropTypes.string.isRequired,
    is_sponsored: PropTypes.bool,
    click_tracker: PropTypes.string,
    impression_tracker: PropTypes.string,
    entries: PropTypes.arrayOf(PropTypes.shape({
      truncated_short_title_80: PropTypes.string,
      url: PropTypes.string,
      label: PropTypes.string,
      contributors: PropTypes.arrayOf(
        PropTypes.shape({
          display_name: PropTypes.string
        })
      )
    })),
    is_sem3_available: PropTypes.bool
  }),
  meta_data: PropTypes.shape({
    meta_title: PropTypes.string,
    meta_description: PropTypes.string
  }),
  analytics: PropTypes.shape({
    url_full: PropTypes.string
  }),
  shoppingNavigation: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        path: PropTypes.string
      })
    )
  })
};
